"use client";

import { useState, useMemo } from "react";
import { HOURS_PER_MONTH, PANEL_URL } from "@/lib/panelPrices";
import type { CalculatorData } from "@/lib/siteContent";
import type { PageTexts } from "@/lib/siteTexts";

interface PricingTier {
  min: number;
  max: number;
  price: number;
}

interface ResourceType {
  id: string;
  name: string;
  unit: string;
  icon: string;
  description: string | null;
  cloudstack_name: string | null;
  hourly_price?: number;
}

interface CalculatorResource {
  id: string;
  resource_type: string;
  resource_type_id: string | null;
  label: string;
  unit: string;
  min_value: number;
  max_value: number;
  step_value: number;
  default_value: number;
  price_per_unit: number;
  pricing_tiers: PricingTier[];
  color: string;
  display_order: number;
}

interface CalculatorItem {
  id: string;
  label: string;
  unit: string;
  icon: string;
  min: number;
  max: number;
  step: number;
  value: number;
  setter: (value: number) => void;
  color: string;
  basePrice: number;
  pricingTiers: PricingTier[];
  cloudstack_name: string | null;
}

function calculateTieredPrice(value: number, pricingTiers: PricingTier[], basePrice: number): number {
  if (!pricingTiers || pricingTiers.length === 0) {
    return value * basePrice;
  }
  const applicableTier = pricingTiers.find(
    tier => value >= tier.min && value <= tier.max
  );
  if (applicableTier) {
    return value * applicableTier.price;
  }
  return value * basePrice;
}

// Wording comes from Admin -> Тексты страниц (pricing.calc.*).
export default function PricingCalculator({ data, texts: t }: { data: CalculatorData; texts: PageTexts<"pricing"> }) {
  const resources = data.resources as CalculatorResource[];
  const resourceTypesMap = data.resourceTypes;
  const error = data.error;

  const [resourceValues, setResourceValues] = useState<Record<string, number>>(() =>
    Object.fromEntries(resources.map((r) => [r.id, r.default_value]))
  );

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleValueChange = (resourceId: string, value: number) => {
    setResourceValues(prev => ({
      ...prev,
      [resourceId]: value
    }));
  };

  const getResourceIcon = (resource: CalculatorResource): string => {
    if (resource.resource_type_id && resourceTypesMap[resource.resource_type_id]) {
      return resourceTypesMap[resource.resource_type_id].icon;
    }
    return '📦';
  };

  const breakdown = useMemo(() => {
    const componentCosts: Record<string, number> = {};
    let totalHourly = 0;

    resources.forEach(resource => {
      const value = resourceValues[resource.id] || resource.default_value;
      const cost = calculateTieredPrice(
        value,
        resource.pricing_tiers || [],
        resource.price_per_unit
      );
      componentCosts[resource.id] = cost;
      totalHourly += cost;
    });

    return {
      componentCosts,
      totalHourly,
      totalMonthly: totalHourly * HOURS_PER_MONTH,
    };
  }, [resources, resourceValues]);

  const calculatorItems: CalculatorItem[] = resources.map(resource => ({
    id: resource.id,
    label: resource.label,
    unit: resource.unit,
    icon: getResourceIcon(resource),
    min: resource.min_value,
    max: resource.max_value,
    step: resource.step_value,
    value: resourceValues[resource.id] || resource.default_value,
    setter: (value: number) => handleValueChange(resource.id, value),
    color: resource.color,
    basePrice: resource.price_per_unit,
    pricingTiers: resource.pricing_tiers || [],
    cloudstack_name: resource.resource_type_id && resourceTypesMap[resource.resource_type_id]
      ? resourceTypesMap[resource.resource_type_id].cloudstack_name
      : null
  }));

  // Get current price for a resource based on its value
  const getCurrentPrice = (item: CalculatorItem): number => {
    if (!item.pricingTiers || item.pricingTiers.length === 0) {
      return item.basePrice;
    }

    const applicableTier = item.pricingTiers.find(
      tier => item.value >= tier.min && item.value <= tier.max
    );

    return applicableTier ? applicableTier.price : item.basePrice;
  };

  // Handle configuration submission
  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setSubmitError(null);
      setSubmitSuccess(false);

      // Prepare configuration data
      const configurationData = {
        resources: calculatorItems.map(item => ({
          id: item.id,
          label: item.label,
          value: item.value,
          unit: item.unit,
          cloudstack_name: item.cloudstack_name,
          pricePerUnit: getCurrentPrice(item),
          totalCost: breakdown.componentCosts[item.id]
        })),
        pricing: {
          totalHourly: breakdown.totalHourly,
          totalMonthly: breakdown.totalMonthly,
        },
        timestamp: new Date().toISOString()
      };
const payload = {
  mode: 'configurator', // или 'trial' / 'template'
  config_json: configurationData, // то, что ты сейчас отправляешь
  // template: 'bitrix_small' // если mode === 'template'
};
      // Send POST request to the panel
      const response = await fetch(`${PANEL_URL}/api/signup-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(payload),
        redirect: 'manual',
      });

      if (response.ok) {
  const data = await response.json();
  const token = data.token;

  // Вариант: редирект на текущем сайте (site.oblako.kg)
  window.location.assign(
    `${PANEL_URL}/auth/redirect/saml2?intent=${encodeURIComponent(token)}`
  );


  return;
} else {
   //   if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setSubmitSuccess(true);
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);

    } catch (err: any) {
      console.error('Error submitting configuration:', err);
      setSubmitError(err.message || t["calc.error"]);
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <section className="py-20 bg-[#0D1526] relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center">
            <p className="text-red-400">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="mt-4 btn-primary py-2 px-6 rounded-lg"
            >
              {t["calc.retry"]}
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-[#0D1526] relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-20" />
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-8 md:mb-12">
          <span className="section-label">{t["calc.label"]}</span>
          <h2 className="font-display font-bold text-3xl md:text-4xl text-white mt-3 mb-4">
            {t["calc.title"]}{" "}
            <span className="gradient-text">{t["calc.titleAccent"]}</span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 md:gap-8">
          {/* Sliders */}
          <div className="lg:col-span-2 space-y-4 md:space-y-5">
            {calculatorItems?.map((item) => {
              const currentPrice = getCurrentPrice(item);
              return (
                <div key={item?.id} className="glass rounded-xl md:rounded-2xl p-4 md:p-6 border border-[rgba(79,124,255,0.12)]">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 md:mb-4 gap-2">
                    <div className="flex-1">
                      <span className="text-lg mr-2">{item?.icon}</span>
                      <span className="text-white font-semibold text-sm md:text-base">{item?.label}</span>
                      <span className="text-[#4A6080] text-xs ml-2 block sm:inline mt-1 sm:mt-0">
                        {currentPrice?.toFixed(2)} сом/ч за {item?.unit}
                      </span>
                    </div>
                    <span className="font-bold text-lg md:text-xl" style={{ color: item?.color }}>
                      {item?.value} {item?.unit}
                    </span>
                  </div>
                  <input
                    type="range"
                    min={item?.min}
                    max={item?.max}
                    step={item?.step}
                    value={item?.value}
                    onChange={(e) => item?.setter(Number(e?.target?.value))}
                    className="w-full h-2 md:h-auto touch-manipulation"
                    style={{
                      WebkitAppearance: 'none',
                      appearance: 'none',
                      cursor: 'pointer'
                    }}
                  />
                  <div className="flex justify-between text-xs text-[#4A6080] mt-1.5">
                    <span>{item?.min} {item?.unit}</span>
                    <span>{item?.max} {item?.unit}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary */}
          <div>
            <div className="glass rounded-xl md:rounded-2xl p-5 md:p-7 neon-border lg:sticky lg:top-28">
              <h3 className="text-white font-bold text-lg md:text-xl mb-4 md:mb-6">{t["calc.summary.title"]}</h3>

              <div className="space-y-3 md:space-y-4 mb-5 md:mb-6">
                {calculatorItems?.map((item) => {
                  const cost = breakdown?.componentCosts[item.id] || 0;
                  return (
                    <div key={item?.id} className="flex justify-between items-center gap-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="text-sm flex-shrink-0">{item?.icon}</span>
                        <span className="text-[#8BA3CC] text-xs md:text-sm truncate">{item?.label}{item?.value ? <span className="text-[#6B7FA3]"> ({item.value})</span> : null}</span>
                      </div>
                      <span className="text-white text-xs md:text-sm font-medium whitespace-nowrap">
                        {cost?.toFixed(2)} сом/ч
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="border-t border-[rgba(79,124,255,0.15)] pt-4 md:pt-5 mb-4 md:mb-5">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-[#8BA3CC] text-xs md:text-sm">{t["calc.summary.hourly"]}</span>
                  <span className="text-white font-bold text-sm md:text-base">{breakdown?.totalHourly?.toFixed(2)} сом</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#8BA3CC] text-xs md:text-sm">{t["calc.summary.monthly"]}</span>
                  <span className="text-xl md:text-2xl font-bold gradient-text">{breakdown?.totalMonthly?.toFixed(2)} сом</span>
                </div>
              </div>

              <button 
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full btn-primary py-3 md:py-4 rounded-xl font-bold text-sm touch-manipulation transition-transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <div className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-white border-r-transparent"></div>
                    <span>{t["calc.sending"]}</span>
                  </>
                ) : (
                  t["calc.submit"]
                )}
              </button>
              
              {submitSuccess && (
                <div className="mt-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <p className="text-green-400 text-xs text-center">✓ {t["calc.success"]}</p>
                </div>
              )}
              
              {submitError && (
                <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-red-400 text-xs text-center">{submitError}</p>
                </div>
              )}
              

            </div>
          </div>
        </div>
      </div>
    </section>
  );
}